import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { TicketService } from '../../../ticket.service';
import { FormModalBaseComponent } from '../../../../../../../core/base/components/form-modal-base/form-modal-base.component';
import { TicketModel } from '../../../../../../../core/models/ticket.model';
import { SnackbarService } from '../../../../../../../core/services/snackbar.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TicketTypeEnum } from '../../../../../../../core/enums/ticket-type.enum';
import { finalize, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EstablishmentService } from '../../../../../management/establishment/establishment.service';
import { AuthService } from '../../../../../../../core/services/auth.service';
import { RequestTypeEnum, requestTypeOptions } from '../../../../../../../core/enums/request-type.enum';
import {
  CompetenceMotifEnum,
  LEGACY_COMPETENCE_MOTIF_IDS,
} from '../../../../../../../core/enums/competence-motif.enum';
import { MetrikMotifService } from '../../../../../settings/metrik-motif/metrik-motif.service';
import { MetrikMotifModel } from '../../../../../../../core/models/metrik-motif.model';
import { MetrikMotifHelper } from '../../../../../../../core/helpers/metrik-motif.helper';
import { DEFAULT_WORKED_DAYS } from '../../../../../../../core/enums/week-day.enum';
import {
  DEFAULT_DAILY_HOURS,
  formatHoursLabel,
  hoursLabelValidator,
  parseHoursLabel,
} from '../../../../../../../core/helpers/duration.helper';

@Component({
  selector: 'ticket-requirement-create-update-delete-component',
  templateUrl: 'ticket-requirement-create-update-delete.component.html',
})
export class TicketRequirementCreateUpdateDeleteComponent
  extends FormModalBaseComponent<TicketModel>
  implements OnInit
{
  establishments$: Observable<any>;
  requestTypeOptions = requestTypeOptions;
  // PRED-3288 : motifs Metrik typés Compétence (référentiel Core)
  metrikMotifs: MetrikMotifModel[] = [];
  readonly today: Date = new Date();
  computedDays: number = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public service: TicketService,
    public dialogRef: MatDialogRef<FormModalBaseComponent<TicketModel>>,
    public snackbarService: SnackbarService,
    private establishmentService: EstablishmentService,
    public authService: AuthService,
    private metrikMotifService: MetrikMotifService,
  ) {
    super(data, dialogRef, service);
  }

  /** Controls only required when the request is of type "Compétence". */
  private readonly competenceControls = [
    'metrikMotif',
    'justification',
    'periodStart',
    'periodEnd',
    'workedDays',
    'dailyHours',
  ];

  ngOnInit() {
    super.ngOnInit();
    this.initForm();
    this.autocompleteEstablishment('').then();
    this.loadMetrikMotifs();

    if (this.data?.preselectedEstablishment) {
      this.form.get('establishment')!.setValue(this.data.preselectedEstablishment);
      this.form.get('establishment')!.disable();
    }

    // Toggle the "Compétence" controls so they only validate when that type is selected.
    this.subscription.add(this.form.get('requestType')!.valueChanges.subscribe(() => this.syncCompetenceControls()));
    this.syncCompetenceControls();
  }

  initForm() {
    this.form = new UntypedFormGroup({
      establishment: new UntypedFormControl(null, [Validators.required]),
      requestType: new UntypedFormControl(
        requestTypeOptions.find(o => o.value === RequestTypeEnum.ACTIVITY),
        [Validators.required],
      ),
      metrikMotif: new UntypedFormControl(null, [Validators.required]),
      justification: new UntypedFormControl(null, [Validators.required]),
      periodStart: new UntypedFormControl(new Date(), [Validators.required]),
      periodEnd: new UntypedFormControl(null, [Validators.required]),
      // PRED-3286 : jours travaillés + heures quotidiennes (libellé « 7h45 »)
      workedDays: new UntypedFormControl([...DEFAULT_WORKED_DAYS], [Validators.required]),
      dailyHours: new UntypedFormControl(formatHoursLabel(DEFAULT_DAILY_HOURS), [
        Validators.required,
        hoursLabelValidator(),
      ]),
    });
  }

  /** Pré-sélectionne « Surcroît » (motif seedé équivalent à l'ancienne valeur par défaut) s'il existe, sinon le premier. */
  private loadMetrikMotifs(): void {
    this.subscription.add(
      this.metrikMotifService.findCompetenceMotifs().subscribe(motifs => {
        this.metrikMotifs = motifs;
        const preferred = motifs.find(m => m._id === LEGACY_COMPETENCE_MOTIF_IDS[CompetenceMotifEnum.INCREASE]);
        this.form.get('metrikMotif')!.setValue(preferred ?? motifs[0] ?? null);
      }),
    );
  }

  isCompetence(): boolean {
    return this.form.get('requestType')!.value?.value === RequestTypeEnum.COMPETENCE;
  }

  /** Disabled controls drop out of both `form.valid` and the payload, so this neutralises
   *  the Compétence validators in Activité mode. */
  private syncCompetenceControls(): void {
    const enabled = this.isCompetence();
    this.competenceControls.forEach(name => {
      const control = this.form.get(name)!;
      enabled ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false });
    });
  }

  createItem() {
    if (this.form.invalid) {
      return;
    }
    const raw = this.form.getRawValue();
    const data: TicketModel = {
      establishment: raw.establishment._id,
      type: TicketTypeEnum.REQUIREMENT,
      requestType: raw.requestType?.value,
    };
    if (this.isCompetence()) {
      data.metrikMotif = MetrikMotifHelper.toRelations([raw.metrikMotif])[0];
      data.justification = raw.justification;
      data.periodStart = raw.periodStart;
      data.periodEnd = raw.periodEnd;
      data.workedDays = raw.workedDays;
      data.dailyHours = parseHoursLabel(raw.dailyHours);
    }
    this.loading = true;
    this.service
      .create(data)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: response => {
          this.snackbarService.success('Ticket créé avec succès');
          this.dialogRef.close(response);
        },
        // Refus métier du back (ex. période sans jour travaillé) : la modale reste ouverte avec la saisie
        error: err => this.snackbarService.warning(err?.error?.message ?? 'La création du ticket a échoué'),
      });
  }

  async autocompleteEstablishment(search = '') {
    const user = this.authService.currentUserValue();
    const establishmentIds = user.affectations?.map(x => x.establishment._id);
    this.establishments$ = this.establishmentService
      .findAllAsSelect(true, 'label', 'desc', search, { includeIds: establishmentIds })
      .pipe(map(res => res.data));
  }
}
